import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

/**
 * NavigateWithQuery Component
 *
 * A wrapper around React Router's Navigate component that preserves query parameters
 * and hash fragments when redirecting to a new route.
 *
 * This is useful for maintaining state like ?sso=true or #section when redirecting users
 * after authentication or other flows.
 *
 * @param {Object} props - Component props
 * @param {string} props.to - The destination path to navigate to
 * @param {boolean} props.replace - Whether to replace the current history entry (default: false)
 * @param {Object} props.state - Optional state to pass to the new location
 *
 * @example
 * // Redirects from "/" to "/overview" while preserving ?sso=true
 * <NavigateWithQuery to="/overview" replace />
 *
 * @example
 * // Redirects and passes state
 * <NavigateWithQuery to="/dashboard" state={{ from: 'login' }} />
 */
const NavigateWithQuery = ({ to, replace = false, state = null, ...props }) => {
  const location = useLocation();

  // Preserve the current query parameters and hash
  const destination = `${to}${location.search}${location.hash}`;

  return (
    <Navigate to={destination} replace={replace} state={state} {...props} />
  );
};

NavigateWithQuery.propTypes = {
  to: PropTypes.string.isRequired,
  replace: PropTypes.bool,
  state: PropTypes.object,
};

export default NavigateWithQuery;
